import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import hero_img from 'assets/BG_full.webp';
import AuthStateWrapper from 'components/common/AuthStateWrapper';
import SubscriptionModal from 'components/modals/SubscriptionModal';
import useModalState from 'hooks/useModalState';
import BuyTokensModal from 'modals/BuyTokensModal';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';
import useQueryParams from 'utils/useQueryParams';

import TrackingAPI from '../../api/TrackingAPI';
import { getAllTags } from '../../components/games/tools/PublicGamesListTool';
import { CharacterProfiles } from '../../components/generate_scenario/tools/CharacterProfiles';
import { CharactersNamesTool } from '../../components/generate_scenario/tools/CharactersNamesTool';
import { ConfirmScenarioTool } from '../../components/generate_scenario/tools/ConfirmScenarioTool';
import { InitialMessagesTool } from '../../components/generate_scenario/tools/InitialMessagesTool';
import { ScenarioLocationTool } from '../../components/generate_scenario/tools/ScenarioLocationTool';
import { ScenarioSetupTool } from '../../components/generate_scenario/tools/ScenarioSetupTool';
import { ScriptIdeaTool } from '../../components/generate_scenario/tools/ScriptIdeaTool';
import NiceConfirm from '../../components/modals/NiceConfirm';
import UserTemplate from '../../components/templates/UserTemplate/UserTemplate';
import { useScenarioApi } from '../../hooks/useScenarioApi';
import { useScenarioModals } from '../../hooks/useScenarioModals';
import { useScenarioState } from '../../hooks/useScenarioState';
import {
  useCreateWizardGameMutation,
  useLazyGetPrivateGamesQuery,
  useLazyGetPublicGamesQuery
} from '../../services/api/gamesApi';
import {
  useGenerateAppearanceMutation,
  useGenerateTitleMutation,
  useIsLegalMutation,
  useRequestAiNamesMutation,
  useRequestCharactersMutation,
  useRequestInitialMessagesMutation,
  useRequestLocationMutation,
  useRequestScenarioMutation,
  useRequestSetupMutation
} from '../../services/api/generateScenarioApi';


const TOTAL_STEPS = 7;

const ScenariosGeneratorPage = () => {
  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const [isOpenSubscriptionModal, openSubscriptionModal, closeSubscriptionModal] = useModalState(false);
  const [isOpenBuyTokensModal, openBuyTokensModal, closeBuyTokensModal] = useModalState(false);
  const initialTags = useMemo(() => {
    try {
      return queryParams?.tags ? JSON.parse(queryParams.tags) : [];
    } catch (_error) {
      return [];
    }
  }, [queryParams?.tags]);

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = currentUser?.hasSubscription === true;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription
      };
    }, [])
  );

  const {
    currentStep,
    setCurrentStep,
    playerName,
    setPlayerName,
    scenarioIdea,
    setScenarioIdea,
    charactersNames,
    setCharactersNames,
    playerDescription,
    setPlayerDescription,
    charactersDescription,
    setCharactersDescription,
    charactersAvatars,
    setCharactersAvatars,
    scenarioName,
    setScenarioName,
    setup,
    setSetup,
    location,
    setLocation,
    initialMessages,
    setInitialMessages,
    selectedTags,
    setSelectedTags,
    picturesGenerated,
    setPicturesGenerated,
    saveDataToStorage,
    setDataFromStorage,
    resetState
  } = useScenarioState(currentUser, initialTags);

  const {
    backConfirmModal,
    showContinueModal,
    onBack,
    handleBackConfirm,
    handleBackCancel,
    handleContinueCancel,
    handleContinueConfirm,
    checkForSavedProgress
  } = useScenarioModals(resetState, setDataFromStorage, setCurrentStep);

  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingCreateScenario, setIsLoadingCreateScenario] = useState(false);
  const [gamesCreated, setGamesCreated] = useState([]);


  const [generateTitle] = useGenerateTitleMutation();
  const [requestAiNames] = useRequestAiNamesMutation();
  const [requestCharacters] = useRequestCharactersMutation();
  const [generateAppearance] = useGenerateAppearanceMutation();
  const [requestSetup] = useRequestSetupMutation();
  const [requestLocation] = useRequestLocationMutation();
  const [requestInitialMessages] = useRequestInitialMessagesMutation();
  const [requestScenario] = useRequestScenarioMutation();
  const [isLegalRequest] = useIsLegalMutation();

  const [getPublicGames] = useLazyGetPublicGamesQuery();
  const [getPrivateGames] = useLazyGetPrivateGamesQuery();
  const [createWizardGame] = useCreateWizardGameMutation();

  const { checkIfLegal, handleScenarioCreation } = useScenarioApi(
    currentUser,
    navigate,
    {
      requestScenario,
      isLegalRequest,
      createWizardGame
    }
  );

  useEffect(() => {
    if (currentStep > 1) {
      saveDataToStorage(currentStep);
    }
  }, [currentStep, saveDataToStorage]);

  useEffect(() => {
    checkForSavedProgress();
    setLoading(true);

    getPublicGames()
      .then(({ data: publicGames }) => {
        const tags = Array.from(getAllTags(publicGames)?.keys());
        setAllTags(tags);
        return getPrivateGames();
      })
      .then(({ data: privateGames }) => {
        setGamesCreated(privateGames || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [checkForSavedProgress, getPublicGames, getPrivateGames]);

  const renderCurrentStep = () => {
    const currentTool = (() => {
      switch (currentStep) {
        case 1:
          return (
            <ScriptIdeaTool
              initialScenarioIdea={scenarioIdea}
              initialPlayerName={playerName}
              initialTags={selectedTags}
              allTags={allTags}
              currentUser={currentUser}
              onChange={(name, idea, tags) => {
                setPlayerName(name);
                setScenarioIdea(idea);
                setSelectedTags(tags);
              }}
              onContinue={async (name, idea, tags) => {
                const [isLegal, { data: aiNames }] = await Promise.all([
                  checkIfLegal({ backstory: idea }),
                  requestAiNames({ scenarioIdea: idea, playerName: name }),
                ]);

                if (!isLegal) {
                  return;
                }

                setPlayerName(name);
                setScenarioIdea(idea);
                setSelectedTags(tags);

                if (aiNames && aiNames['ai-names']) {
                  setCharactersNames(aiNames['ai-names']);
                }
                TrackingAPI.trackEvent('custom_scenario_idea');
                setCurrentStep(currentStep + 1);
              }}
            />
          );
        case 2:
          return (
            <CharactersNamesTool
              initialPlayerName={playerName}
              initialCharactersNames={charactersNames}
              onChange={setCharactersNames}
              onBack={() => {
                onBack(() => {
                  setCharactersNames([]);
                  setCurrentStep(currentStep - 1);
                });
              }}
              onContinue={async characters => {
                setCharactersNames(characters);
                const { data: charactersInfo } = await requestCharacters({
                  scenarioIdea,
                  playerName,
                  aiNames: characters
                });
                const charactersDescription = charactersInfo?.characters;
                setCharactersDescription(charactersDescription);

                const avatars = {};
                await Promise.allSettled(
                  Object.keys(charactersDescription)?.map(name =>
                    generateAppearance({ text: charactersDescription[name], name })
                  )
                ).then(results => {
                  results.forEach(res => {
                    if (res.status === 'fulfilled' && res.value) {
                      avatars[res.value.data.name] = 'gap';
                    }
                  });
                });
                setCharactersAvatars(avatars);
                TrackingAPI.trackEvent('custom_scenario_characters_names');
                setCurrentStep(currentStep + 1);
              }}
            />
          );
        case 3:
          return (
            <CharacterProfiles
              hasSubscription={hasSubscription}
              initialPlayerDescription={playerDescription}
              initialCharactersDescription={charactersDescription}
              initialCharactersAvatars={charactersAvatars}
              onOpenBuyTokens={openBuyTokensModal}
              onOpenSubscriptionModal={openSubscriptionModal}
              onChange={(player, characters, avatars) => {
                setPlayerDescription(player);
                setCharactersDescription(characters);
                setCharactersAvatars(avatars);
              }}
              onBack={() => {
                onBack(() => {
                  setCharactersDescription({});
                  setCharactersAvatars({});
                  setCurrentStep(currentStep - 1);
                });
              }}
              picturesGenerated={picturesGenerated}
              onGenerate={() => {
                setPicturesGenerated(picturesGenerated + 1);
              }}
              onContinue={async (player, characters, avatars) => {
                const [{ data: initialSetup }, playerLegal, ...charactersLegal] = await Promise.all([
                  requestSetup({
                    scenarioIdea,
                    playerName,
                    aiNames: charactersNames,
                    characters
                  }),
                  checkIfLegal({ backstory: player }),
                  ...Object.keys(characters).map(name =>
                    checkIfLegal({ backstory: characters[name] })
                  )
                ]);

                if (!playerLegal || charactersLegal.includes(false)) {
                  errorAlert(
                    'Restricted content. Terms of use violation. Please try different description.'
                  );
                  return;
                }

                TrackingAPI.trackEvent('custom_scenario_character_description');
                setPlayerDescription(player);
                setCharactersDescription(characters);
                setCharactersAvatars(avatars);

                setSetup(initialSetup);
                setCurrentStep(currentStep + 1);
              }}
              showPremiumModal={() => { }}
            />
          );
        case 4:
          return (
            <ScenarioSetupTool
              initialSetup={setup}
              onBack={() => {
                onBack(() => {
                  setSetup('');
                  setCurrentStep(currentStep - 1);
                });
              }}
              onChange={setSetup}
              onContinue={async scriptSetup => {
                setSetup(scriptSetup);
                const { data: initialLocation } = await requestLocation({
                  scenarioIdea,
                  playerName,
                  setup: scriptSetup,
                  characters: charactersDescription
                });
                setLocation(initialLocation);
                setCurrentStep(currentStep + 1);
              }}
            />
          );
        case 5:
          return (
            <ScenarioLocationTool
              initialLocation={location}
              onChange={setLocation}
              onBack={() => {
                onBack(() => {
                  setLocation('');
                  setCurrentStep(currentStep - 1);
                });
              }}
              onContinue={async scriptLocation => {
                setLocation(scriptLocation);
                const { data: messages } = await requestInitialMessages({
                  scenarioIdea,
                  playerName,
                  aiNames: charactersNames,
                  setup,
                  characters: charactersDescription,
                  location: scriptLocation
                });
                setInitialMessages(messages);
                setCurrentStep(currentStep + 1);
              }}
            />
          );
        case 6:
          return (
            <InitialMessagesTool
              messages={initialMessages}
              charactersAvatars={charactersAvatars}
              charactersNames={charactersNames}
              playerName={playerName}
              onChange={setInitialMessages}
              onBack={() => {
                onBack(() => {
                  setInitialMessages([]);
                  setCurrentStep(currentStep - 1);
                });
              }}
              onContinue={async messages => {
                const [{ data: generatedTitle }, ...legalResults] = await Promise.all([
                  generateTitle({
                    scenarioIdea,
                    playerName,
                    charactersNames,
                    setup
                  }),
                  ...messages.map(x => checkIfLegal({ backstory: x?.text }))
                ]);

                if (legalResults.includes(false)) {
                  errorAlert(
                    'Restricted content. Terms of use violation. Please try different messages.'
                  );
                  return;
                }

                TrackingAPI.trackEvent('custom_scenario_initial_messages');
                setInitialMessages(messages);

                let name = generatedTitle?.replace(/(\\)|(\")/g, '');
                if (name.startsWith('Title:')) {
                  name = name.replace('Title:', '');
                }
                setScenarioName(name?.trim());

                setCurrentStep(currentStep + 1);
              }}
            />
          );
        case 7:
          return (
            <ConfirmScenarioTool
              initialScenarioName={scenarioName}
              scenarioIdea={scenarioIdea}
              playerName={playerName}
              playerDescription={playerDescription}
              charactersNames={charactersNames}
              charactersDescription={charactersDescription}
              charactersAvatars={charactersAvatars}
              isLoadingCreateScenario={isLoadingCreateScenario}
              setup={setup}
              location={location}
              onChange={setScenarioName}
              onBack={() => {
                onBack(() => {
                  setScenarioName('');
                  setCurrentStep(currentStep - 1);
                });
              }}
              onStart={async scriptName => {
                setIsLoadingCreateScenario(true);
                await handleScenarioCreation({
                  scriptName,
                  scenarioIdea,
                  playerName,
                  charactersNames,
                  setup,
                  charactersDescription,
                  location,
                  initialMessages,
                  charactersAvatars,
                  gamesCreated,
                  playerDescription
                });
                setIsLoadingCreateScenario(false);
              }}
            />
          );
        default:
          return null;
      }
    })();

    return (
      <StepContent>
        <ContentSection>
          {currentTool}
        </ContentSection>
      </StepContent>
    );
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <AuthStateWrapper requireAuth>
      <UserTemplate currentName={'scenarios_generator'} hasFooter={false} hasHero={false} stickyHeader={true}>

        <PageContainer>
          <ContentWrapper>
            <HeaderBlock>
              <Title>Create scenario</Title>
              <ProgressWrapper>
                {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                  <ProgressBar
                    key={`step_${index + 1}`}
                    active={index + 1 === currentStep}
                  />
                ))}
              </ProgressWrapper>
            </HeaderBlock>
            <StepContainer>
              <StepContent>
                {renderCurrentStep()}
              </StepContent>
            </StepContainer>
          </ContentWrapper>

          {backConfirmModal && (
            <NiceConfirm
              cancelText={'Cancel'}
              confirmText={'Ok'}
              heading={'Back'}
              subHeading={
                'Are you sure you want to go back? You will lose the data you filled in at this step'
              }
              onCancel={handleBackCancel}
              onConfirm={handleBackConfirm}
            />
          )}
          {isOpenSubscriptionModal && (
            <SubscriptionModal
              isOpen={openSubscriptionModal}
              onClose={closeSubscriptionModal}
            />
          )}
          {isOpenBuyTokensModal && (
            <BuyTokensModal
              isOpen
              onClose={closeBuyTokensModal}
              analyticsDataRenderReason='custom_scenario_hire_writer'
            />
          )}
          {showContinueModal && (
            <NiceConfirm
              canClose={false}
              heading={'Continue editing?'}
              subHeading={
                <div>
                  <p className="leading-5">
                    You've stopped at step {showContinueModal.step}. Would you like to continue
                    editing or create a new scenario?
                  </p>
                  <p className="pt-3 text-sm leading-4 text-gray-400">
                    If you decide to create a new scenario your progress will be lost.
                  </p>
                </div>
              }
              confirmText={'Continue'}
              cancelText={'Create new'}
              onCancel={handleContinueCancel}
              onConfirm={handleContinueConfirm}
            />
          )}
        </PageContainer>
      </UserTemplate>
    </AuthStateWrapper>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background-image: url(${hero_img});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: #0D0D0D;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  color: white;
  position: relative;

  @media (min-width: 769px) {
    padding: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: #0D0D0D;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 769px) {
    gap: 32px;
    padding-top: 80px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    height: 100vh;
    position: relative;
    padding-top: 64px;
  }
`;

const HeaderBlock = styled.div`
  @media (max-width: 768px) {
    background: #1A1A1A;
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-family: Raleway;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    padding: 16px 16px 0;
    margin: 0;
  }
`;

const ProgressWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 12px 16px 16px;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background-color: ${props => props.active ? 'rgba(255, 126, 85, 1)' : 'rgba(102, 102, 102, 1)'};
  border-radius: 2px;
  transition: background-color 0.3s ease;
`;

const StepContainer = styled.div`
  background-color: #1A1A1A;
  border-radius: 16px;
  padding: 24px;
  width: 100%;

  @media (max-width: 768px) {
    position: absolute;
    top: 64px;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 0;
    padding: 16px;
    padding-top: 120px;
    padding-bottom: 16px;
    overflow-y: auto;
  }
`;

const StepContent = styled.div`
  @media (max-width: 768px) {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const ContentSection = styled.div`
  @media (max-width: 768px) {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

export const StepTitle = styled.h2`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
`;


export default ScenariosGeneratorPage;